import type { DestinationDTO } from '@/shared/api/mappers/destination-mapper'
import type {
  ApiResponse,
  ApiResponseEnvelope,
  PaginatedResponse,
} from '@/shared/api/types/api-response'
import type {
  CreateDestinationPayload,
  DestinationListParams,
  DestinationListResponse,
  IDestination,
  ILocalizedDestinationMap,
  JsonArray,
  UpdateDestinationPayload,
} from '@/shared/api/types/destination-types'
import { GW, toQueryString } from '@/shared/api/endpoints'
import { mapDestinationDto } from '@/shared/api/mappers/destination-mapper'
import { buildLanguageHeaderConfig } from '@/shared/api/utils/language-header'
import { unwrapApiEnvelope } from '@/shared/api/utils/unwrapapi'
import { request } from '@/shared/libs/request'

const DESTINATIONS_PATH = 'destinations'
const DESTINATION_ADMIN_PATH = 'admin/destinations'

interface DestinationRequestPayload {
  country_id?: string | null
  province_id?: string | null
  slug?: string | null
  destination_type?: string | null
  center_point?: JsonArray | null
  area_boundary?: JsonArray | null
  banner?: string | null
  thumbnail?: string | null
  is_active?: boolean | null
  localizedData?: ILocalizedDestinationMap | null
}

function mapDestinationPayloadToRequest(
  payload: CreateDestinationPayload | UpdateDestinationPayload,
): DestinationRequestPayload {
  return {
    ...(payload.countryId !== undefined ? { country_id: payload.countryId } : {}),
    ...(payload.provinceId !== undefined ? { province_id: payload.provinceId } : {}),
    ...(payload.slug !== undefined ? { slug: payload.slug } : {}),
    ...(payload.destinationType !== undefined ? { destination_type: payload.destinationType } : {}),
    ...(payload.centerPoint !== undefined ? { center_point: payload.centerPoint } : {}),
    ...(payload.areaBoundary !== undefined ? { area_boundary: payload.areaBoundary } : {}),
    ...(payload.banner !== undefined ? { banner: payload.banner } : {}),
    ...(payload.thumbnail !== undefined ? { thumbnail: payload.thumbnail } : {}),
    ...(payload.isActive !== undefined ? { is_active: payload.isActive } : {}),
    ...(payload.localizedData !== undefined ? { localizedData: payload.localizedData } : {}),
  }
}

export async function listDestinations(
  params: DestinationListParams,
): Promise<DestinationListResponse> {
  const qs = toQueryString({
    current: params.current,
    pageSize: params.pageSize,
    ...(params.isActive !== undefined && params.isActive !== null
      ? { isActive: params.isActive }
      : {}),
    ...(params.destinationType ? { destinationType: params.destinationType } : {}),
    ...(params.countryId ? { countryId: params.countryId } : {}),
    ...(params.provinceId ? { provinceId: params.provinceId } : {}),
  })
  const response = await request.get<ApiResponseEnvelope<PaginatedResponse<DestinationDTO>>>(
    GW.masterdata(`${DESTINATIONS_PATH}?${qs}`),
    buildLanguageHeaderConfig(params.languageCode),
  )
  const data = unwrapApiEnvelope(response.data)
  return {
    data: {
      data: data.data.map(mapDestinationDto),
      total: data.total,
      current: data.current,
      pageSize: data.pageSize,
      keyword: data.keyword ?? null,
    },
  }
}

export async function getDestinationById(id: string): Promise<ApiResponse<IDestination>> {
  const response = await request.get<ApiResponseEnvelope<DestinationDTO>>(
    GW.masterdata(`${DESTINATION_ADMIN_PATH}/${id}`),
  )
  const data = unwrapApiEnvelope(response.data)
  return { data: mapDestinationDto(data) }
}

export async function createDestination(
  payload: CreateDestinationPayload,
): Promise<ApiResponse<IDestination>> {
  const response = await request.post<ApiResponseEnvelope<DestinationDTO>>(
    GW.masterdata(DESTINATIONS_PATH),
    mapDestinationPayloadToRequest(payload),
  )
  const data = unwrapApiEnvelope(response.data)
  return { data: mapDestinationDto(data), message: 'Destination created successfully' }
}

export async function updateDestination(
  id: string,
  payload: UpdateDestinationPayload,
): Promise<ApiResponse<IDestination>> {
  const response = await request.patch<ApiResponseEnvelope<DestinationDTO>>(
    GW.masterdata(`${DESTINATIONS_PATH}/${id}`),
    mapDestinationPayloadToRequest(payload),
  )
  const data = unwrapApiEnvelope(response.data)
  return { data: mapDestinationDto(data), message: 'Destination updated successfully' }
}

export async function deleteDestination(id: string): Promise<ApiResponse<null>> {
  await request.delete(GW.masterdata(`${DESTINATIONS_PATH}/${id}`))
  return { data: null, message: 'Destination deleted successfully' }
}
